/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppBar, Box, createStyles, Divider, Drawer, IconButton, List, ListItem, makeStyles, Theme, Toolbar, Typography, useMediaQuery } from '@material-ui/core';
import { Close as CloseIcon, Menu as MenuIcon } from '@material-ui/icons';
import cn from 'classnames';
import * as React from 'react';
import { Route, useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import PersonMaleIcon from '../../assets/icons/PersonMaleIcon';
import { useBrandingContext } from '../../branding';
import { useBankContext, useBrandContext } from '../../dictionaries/context/BankContext';
import { BankType, BrandType } from '../../generated/graphql';
import { getBankImage } from '../../utils/getBankImage';
import { useGetProfile } from '../../utils/useGetProfile';
import { useLocalAdminInfo } from '../../utils/useLocalAdminInfo';
import { useLogout } from '../../utils/useLogout';
import Btn from '../Btn/Btn';
import NegotiationTestOverrideForm from '../NegotiationTestOverride/NegotiationTestOverrideForm';
import { MenuItemKeys, PORTAL_MENU_ITEMS } from './portalMenuItems';

interface PortalMenuProps {
  parentUrl: string;
  parentPath: string;
  inIdVerification: boolean;
}

const PortalMenu: React.FC<PortalMenuProps> = ({ parentUrl, parentPath, inIdVerification }) => {
  const [ adminInfo ] = useLocalAdminInfo();
  const isAdmin = !!adminInfo.token;
  const classes = useStyles({ isAdmin });
  const { data: profileData } = useGetProfile();
  const hasProfile = !!profileData?.profile;
  const logout = useLogout();
  const handleLogout = () => logout();
  const isWeb = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [ open, setOpen ] = React.useState(false);
  const handleToggle = () => setOpen((prev) => !prev);
  const history = useHistory();
  const [ brand ] = useBrandContext();
  const [ bank ] = useBankContext();

  const getText = (
    text: string, key?: MenuItemKeys,
  ) => {
    const newText = brand === BrandType.Tranquiapp
    && bank === BankType.BancoDeBogota
    && key === MenuItemKeys.DEBTS_NEGOTIATION
      ? 'Acuerdos de recuperación de cartera' : text;
    return newText;
  };

  const menuItems = React.useMemo(() => {
    return PORTAL_MENU_ITEMS;
  }, []);

  React.useEffect(() => {
    history.listen(() => setOpen(false));
  }, [ history ]);

  const content = (
    <Box className={classes.root}>
      <Logo cnInner="mb-4" withLink={!inIdVerification} />
      {isAdmin && <Typography variant="h1" className="mb-2">⚠️ ADMIN</Typography>}

      {hasProfile && (
        <>
          <Typography className={classes.cap}>CUENTA</Typography>
          <Box className={classes.profileBox}>
            <PersonMaleIcon className={classes.profileIcon} />
            <Box>
              <Typography variant="h3">
                {profileData?.profile?.firstname}
                {' '}
                {profileData?.profile?.lastname}
              </Typography>
              {profileData?.profile?.email && (
              <Typography>
                <small>{profileData?.profile?.email}</small>
              </Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      <Typography className={classes.cap}>HERRAMIENTAS</Typography>
      <List component="nav">
        {menuItems.map(({ key, icon: Icon, text, path, external = false }) => {
          if (external) {
            return (
              <ListItem
                button
                component="a"
                href={path}
                className={cn(classes.item)}
                data-test={`cy-${text.split(' ').join('')}-menuItemExternal`}
              >
                { Icon ? <Icon className={classes.itemIcon} /> : null }
                <Typography className={classes.itemText}>{getText(text, key)}</Typography>
              </ListItem>
            );
          }

          return (
            <Route
              path={`${parentPath}${path}`}
              key={text}
              exact
            >
              {({ match }) => (
                <ListItem
                  button
                  component={RouterLink}
                  to={`${parentUrl}${path}`}
                  className={cn(classes.item, match && classes.itemActive)}
                  data-test={`cy-${text.split(' ').join('')}-menuItem`}
                >
                  { Icon ? <Icon className={classes.itemIcon} /> : null }
                  <Typography className={classes.itemText}>{getText(text, key)}</Typography>
                </ListItem>
              )}
            </Route>
          );
        })}
      </List>

      <NegotiationTestOverrideForm onClose={handleToggle} />

      <Box mt="auto">
        {hasProfile && <Btn onClick={handleLogout} variant="text" className={classes.textBtn} data-test="cy-btn-logout-portal">Cerrar sesión</Btn>}
      </Box>
    </Box>
  );

  if (inIdVerification && isWeb) return null;
  const bankImage = bank && getBankImage(bank);

  return (
    <>
      {isWeb && content}
      {!isWeb && (
      <>
        <AppBar position="relative" color="transparent" elevation={2}>
          <Toolbar className={classes.toolbar}>
            {!inIdVerification
            && (
            <IconButton data-test="cy-burgerMenu-btn" edge="start" aria-label="menu" onClick={handleToggle}>
              <MenuIcon />
            </IconButton>
            )}
            <Logo cn={classes.logoMobile} withLink={!inIdVerification} />
            {bankImage && (
            <>
              <Divider orientation="vertical" className="greenDivider" flexItem />
              <img style={{ height: 25 }} src={bankImage} alt="bank" />
            </>
            )}
          </Toolbar>
        </AppBar>
        <Drawer anchor="left" open={open} onClose={handleToggle} ModalProps={{ keepMounted: true }} PaperProps={{ className: classes.drawerPaper }}>
          {content}
          <IconButton aria-label="cerrar" onClick={handleToggle} className={cn(classes.closeBtn, !open && classes.closeBtnHidden)}>
            <CloseIcon />
          </IconButton>
        </Drawer>
      </>
      )}
    </>
  );
};

const Logo: React.FC<{ cn?: string, cnInner?: string, withLink: boolean }> = (props) => {
  const { cn: cnProp, cnInner, withLink } = props;
  const { logo: BrandLogo, homepageUrl } = useBrandingContext();
  const logo = <BrandLogo className={cnInner} />;
  if (!withLink) return logo;
  return (
    <a
      href={homepageUrl}
      target="_blank"
      rel="noreferrer"
      className={cnProp}
    >
      {logo}
    </a>
  );
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    width: 330,
    maxWidth: 'calc(100vw - 58px)',
    flexShrink: 0,
    backgroundColor: ({ isAdmin }: any) => (
      isAdmin ? theme.palette.error.main : theme.palette.marble.main
    ),
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    [theme.breakpoints.between('md', 'md')]: {
      width: 230,
    },
  },
  profileBox: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
  },
  item: {
    borderRadius: 5,
    marginBottom: 2,
    padding: theme.spacing(2),
  },
  itemActive: {
    backgroundColor: theme.palette.bg3.default,
  },
  logoMobile: {
    transform: 'translate(0px, 2px)',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 4),
    '& .greenDivider': {
      border: `1px solid${theme.palette.primary.main}`,
      margin: theme.spacing(0, 2),
    },
  },
  itemIcon: {
    width: 20,
    marginRight: theme.spacing(),
  },
  itemText: {
    lineHeight: 1,
  },
  cap: {
    ...theme.typography.capitalize as any,
    marginTop: 8,
    color: theme.palette.text5.main,
  },
  profileIcon: {
    height: 55,
    width: 55,
    border: '3px solid white',
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
  textBtn: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
  },
  drawerPaper: {
    overflow: 'visible',
  },
  closeBtn: {
    position: 'absolute',
    left: '100%',
    borderRadius: 0,
    borderBottomRightRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.marble.main,
    transition: 'transform .3s ease',
  },
  closeBtnHidden: {
    transform: 'translate(-100%, 0px)',
  },
}));

export default PortalMenu;
